/**
 * Contract annotation kind ⇄ `scichart-financial-tools` class name.
 *
 * **The one place a vendor's class name meets a contract kind**, and the reason
 * it is one place is the same reason the contract says `pitchfork` rather than
 * `PitchforkAnnotation`: the schema is generated into TypeScript *and* .NET, and
 * the native adapters read it too. Spreading one JavaScript package's vocabulary
 * through a cross-platform contract would make every other renderer inherit a
 * dependency it does not have — and would make renaming a class in that package
 * a schema migration.
 *
 * So the contract names what a drawing *is*, and this file says which class
 * draws it here. A renderer that cannot draw a kind looks it up, finds nothing,
 * and draws nothing — which is the correct outcome and needs no coordination.
 *
 * ## Both directions are live
 *
 * `tradingClassFor` arms a placement and rebuilds a stored drawing;
 * `kindForTradingClass` is how a placement that has just finished is recognised
 * on the way back into the contract. `scichart-builder` uses the class name for
 * a third thing too — SciChart registers every one of these under exactly this
 * string, so the map doubles as the constructor table and there is no separate
 * list of imports to fall out of step with it.
 */

import type { Annotation } from "@qufolio/model";

/** A contract kind drawn by the financial-tools palette rather than by us. */
export type TradingAnnotationKind = Extract<
  Annotation["type"],
  | "extendedLine"
  | "horizontalLine"
  | "verticalLine"
  | "crossLine"
  | "angleLine"
  | "polyline"
  | "sector"
  | "channel"
  | "flatBottomChannel"
  | "disjointChannel"
  | "pitchfork"
  | "schiffPitchfork"
  | "modifiedSchiffPitchfork"
  | "insidePitchfork"
  | "pitchfan"
  | "fibRetracement"
  | "fibExtension"
  | "fibCircles"
  | "fibSpeedResistanceArcs"
  | "fibWedge"
  | "fibTimeZones"
  | "cyclicLines"
  | "cyclicArcs"
  | "stopLossTakeProfit"
  | "measure"
>;

/**
 * Contract kind → the library class that draws it.
 *
 * Written as strings rather than as `ETradingAnnotationType` members so this
 * module needs no runtime import of the library — it is consulted by the
 * palette and by the persistence path, neither of which should pull in a
 * charting bundle to answer a naming question. `scichart-builder` validates
 * every value against the real enum at arm time, and a test pins the two lists
 * against each other so a rename in the package cannot pass silently.
 */
export const TRADING_ANNOTATION_CLASS: Readonly<Record<TradingAnnotationKind, string>> = {
  extendedLine: "ExtendedLineAnnotation",
  horizontalLine: "HorizontalTrendLineAnnotation",
  verticalLine: "VerticalTrendLineAnnotation",
  crossLine: "CrossLineAnnotation",
  angleLine: "AngleLineAnnotation",
  polyline: "PolyLineAnnotation",
  sector: "SectorAnnotation",
  channel: "ChannelAnnotation",
  flatBottomChannel: "FlatBottomChannelAnnotation",
  disjointChannel: "DisjointChannelAnnotation",
  pitchfork: "PitchforkAnnotation",
  schiffPitchfork: "SchiffPitchforkAnnotation",
  modifiedSchiffPitchfork: "ModifiedSchiffPitchforkAnnotation",
  insidePitchfork: "InsidePitchforkAnnotation",
  pitchfan: "PitchfanAnnotation",
  fibRetracement: "FibonacciRetracementAnnotation",
  fibExtension: "FibonacciExtensionAnnotation",
  fibCircles: "FibonacciCirclesAnnotation",
  fibSpeedResistanceArcs: "FibonacciSpeedResistanceArcsAnnotation",
  fibWedge: "FibonacciWedgeAnnotation",
  fibTimeZones: "FibonacciTimeZoneAnnotation",
  cyclicLines: "CyclicLineAnnotation",
  cyclicArcs: "CyclicArcAnnotation",
  stopLossTakeProfit: "StopLossTakeProfitAnnotation",
  measure: "MeasureAnnotation",
};

/**
 * The multi-point kinds with an interior to paint.
 *
 * Measured against the package rather than guessed: `ChannelAnnotation` declares
 * `fill` and the two other channels extend it, `CyclicArcAnnotation` and
 * `PolyLineAnnotation` declare their own. **Every other kind ignores it** — a
 * pitchfork's shading is `fullWidthZoneFill`, which is a different property with
 * different semantics and is not what a "fill colour" control means.
 *
 * Here rather than in the control, because which kinds have an inside is a fact
 * about the vendor's classes, and this file is where those are allowed to be
 * known. A control offering a fill that does nothing is worse than no control.
 */
export const TRADING_FILLABLE: ReadonlySet<TradingAnnotationKind> = new Set<TradingAnnotationKind>(
  ["channel", "flatBottomChannel", "disjointChannel", "cyclicArcs", "polyline"],
);

/** The library class for one contract kind, or `undefined` if we do not draw it. */
export function tradingClassFor(kind: Annotation["type"]): string | undefined {
  return TRADING_ANNOTATION_CLASS[kind as TradingAnnotationKind];
}

/** Reverse: which contract kind a library class is, for reading a placement back. */
export function kindForTradingClass(className: string): TradingAnnotationKind | undefined {
  for (const [kind, name] of Object.entries(TRADING_ANNOTATION_CLASS)) {
    if (name === className) return kind as TradingAnnotationKind;
  }
  return undefined;
}

/**
 * Whether this kind is drawn by the palette rather than by our own modifier.
 *
 * `freehand` is deliberately **not** here: it is a contract kind we already
 * store and render ourselves, and the library's own freehand is a different
 * drawing with a different modifier. Two things that look the same and are not.
 */
export function isTradingKind(kind: Annotation["type"]): kind is TradingAnnotationKind {
  return kind in TRADING_ANNOTATION_CLASS;
}
